import { injectable, inject } from 'inversify';
import { IBraze } from '@grubhubprod/umami-library/dist/braze/types';
import { loadBraze } from 'src/preload/loadBraze';
import { Platform } from 'src/environment';
import { NativeMessages } from 'src/native-messages';
import { TYPES } from '../di/types';
import { isDesktopViewport } from '../utilities/screenWidth.utility';
import type { NativeBridge } from '../utilities/native-bridge.utility';
import {
  sanitizeInput,
  createDinerCustomEvent,
  getBrazeSdkUrl,
} from '../utilities/braze-service.utility';
import { appConfigFactory } from '../factories/appConfig.factory';
import { apiClient } from './NativeApiClient';
import type { GrubTelemetryService } from './monitoring/GrubTelemetryService';
import type { CredentialService } from '.';

export enum BRAZE_PLATFORM {
  WEB = 'desktop-web',
  MOBILE = 'mobile-web',
}

export interface BrazeEvent {
  name: string;
  data: any | BrazeSubscriptionEventData;
}

export interface BrazeWebEvent {
  name: string;
  data: BrazeWebEventData;
}

export interface BrazeNativeEvent {
  name: string;
  properties: unknown;
}

export interface BrazeWebEventData {
  platform?: BRAZE_PLATFORM;
  currentRoute?: string;
  loggedIn?: boolean;
}

export interface BrazeSubscriptionEventData {
  cost_amount: string;
  max_usages: string;
  period_in_days: string;
  trial_duration: string;
  trial_duration_unit: string;
  subscription_description: string;
  purchase_placement?: string;
}

export interface BrazeWebPayload extends BrazeWebEvent {}

export interface BrazeService {
  sendBrazeEvent(event: BrazeEvent): void;
}

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class BrazeServiceImpl implements BrazeService {
  private braze: IBraze;
  private brazeLoaded: Promise<unknown> = Promise.resolve();
  private configuration: AppConfig = appConfigFactory();

  constructor(
    @inject(TYPES.Window) private readonly windowInstance: Window,
    @inject(TYPES.CredentialsService)
    private readonly credentialsService: CredentialService,
    @inject(TYPES.AppConfig)
    private readonly appConfig: AppConfig,
    @inject(TYPES.NativeBridge)
    private readonly nativeBridge: NativeBridge,
    @inject(TYPES.GrubTelemetryService)
    private readonly grubTelemetryService: GrubTelemetryService,
  ) {
    this.init();
  }

  private async init(): Promise<void> {
    if (!this.windowInstance.braze) {
      const brazeSdkUrl = getBrazeSdkUrl(this.configuration.environment);
      this.brazeLoaded = loadBraze(brazeSdkUrl);
    }

    await this.brazeLoaded;

    this.braze = this.windowInstance.braze;
  }

  public async sendBrazeEvent(event: BrazeEvent): Promise<void> {
    await this.brazeLoaded;
    if (this.appConfig.platform === Platform.WEB) {
      this.sendBrazeWebEvent(event as BrazeWebEvent);
    } else {
      this.sendBrazeNativeEvent({
        name: event.name,
        properties: event.data,
      } as BrazeNativeEvent);
    }
  }

  private async sendBrazeWebEvent(event: BrazeWebEvent): Promise<void> {
    const isDesktop = isDesktopViewport(this.windowInstance);

    const brazeData: BrazeWebEventData = {
      ...event.data,
      platform: isDesktop ? BRAZE_PLATFORM.WEB : BRAZE_PLATFORM.MOBILE,
      currentRoute: this.windowInstance.location.pathname || '',
      loggedIn: this.credentialsService.isLoggedIn(),
    };

    for (const field in brazeData) {
      if (brazeData.hasOwnProperty(field)) {
        brazeData[field] = sanitizeInput(brazeData[field]);
      }
    }
    const payload: BrazeWebPayload = {
      name: sanitizeInput(event.name),
      data: brazeData,
    };

    this.grubTelemetryService.sendPageActionEvent({
      type: 'braze_event',
      payload: {
        braze_feature: 'event',
        event_name: event.name,
        ...event.data,
      },
    });

    const account = this.credentialsService.getCredentials();
    if (account.credential.ud_id) {
      const dinerCustomEvent = createDinerCustomEvent({
        eventName: payload.name,
        brandName: this.configuration.brand,
        eventProperties: payload.data,
      });
      await apiClient.post(
        '/consumer-engagement/diner-events',
        dinerCustomEvent,
      );
    } else {
      this.braze.logCustomEvent(
        payload.name,
        payload.data as Record<string, unknown>,
      );
      this.braze.requestImmediateDataFlush();
    }
  }

  private sendBrazeNativeEvent(event: BrazeNativeEvent): void {
    this.nativeBridge.toNative(NativeMessages.BRAZE_CUSTOM_EVENT, event);
  }
}
